/**
 * Zustand 对话状态管理
 * 使用 devtools + persist 中间件
 * 匹配后端 WebSocket 协议
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { ChatMessage, Conversation } from '../services/types';
import { chatApi } from '../services/api';

// 定义会话类别枚举
export enum ConversationCategory {
  PRODUCT_INQUIRY = '产品咨询',
  TECHNICAL_SUPPORT = '技术支持',
  AFTER_SALES = '售后服务',
  OTHER = '其他',
}

interface ChatStore {
  // 状态
  messages: ChatMessage[];
  isConnected: boolean;
  isTyping: boolean;
  conversationId: string | null;

  // 会话管理
  sessions: Conversation[];
  currentSessionId: string | null;
  isLoadingSessions: boolean;

  // Widget UI 状态
  isHistoryModalOpen: boolean;      // 历史记录模态框开关
  pendingSession: boolean;           // 标记是否为延迟创建的会话
  deletingSessionId: string | null;  // 待删除的会话ID
  isConfirmDialogOpen: boolean;      // 确认对话框开关

  // 新增：失败计数和人工客服控制
  failureCount: number;
  shouldShowHumanOption: boolean;
  currentCategory?: ConversationCategory;

  // 匿名用户支持
  anonymousId: string | null;

  // 操作
  sendMessage: (content: string, stream?: boolean) => Promise<void>;
  addMessage: (message: ChatMessage) => void;
  updateStreamingMessage: (id: string, content: string) => void;
  completeStreamingMessage: (
    id: string,
    intent?: string,
    confidence?: number,
    suggestedActions?: string[]
  ) => void;
  clearHistory: () => void;

  // 会话操作
  createSession: () => Promise<string>;
  fetchSessions: () => Promise<void>;
  fetchHistory: (sessionId: string) => Promise<void>;
  switchSession: (sessionId: string) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;

  // Widget UI 操作
  openHistoryModal: () => Promise<void>;
  closeHistoryModal: () => void;
  startNewSession: () => void;
  requestDeleteSession: (sessionId: string) => void;
  confirmDeleteSession: (sessionId?: string) => Promise<void>;
  cancelDeleteSession: () => void;

  // WebSocket 连接管理
  connect: () => void;
  disconnect: () => void;

  // AI 状态
  setTyping: (isTyping: boolean) => void;
  setConnected: (isConnected: boolean) => void;
  setConversationId: (conversationId: string | null) => void;
  setCurrentSessionId: (sessionId: string | null) => void;

  // 新增方法
  incrementFailureCount: () => void;
  resetFailureCount: () => void;
  reportUnsatisfied: () => void;
  setShouldShowHumanOption: (show: boolean) => void;
  setCurrentCategory: (category: ConversationCategory) => void;

  // 匿名用户支持
  ensureAnonymousId: () => Promise<string>;
}

export const useChatStore = create<ChatStore>()(
  devtools(
    persist(
      (set, get) => ({
        // 初始状态
        messages: [],
        isConnected: false,
        isTyping: false,
        conversationId: null,
        sessions: [],
        currentSessionId: null,
        isLoadingSessions: false,

        // Widget UI 状态
        isHistoryModalOpen: false,
        pendingSession: false,
        deletingSessionId: null,
        isConfirmDialogOpen: false,

        // 新增字段默认值
        failureCount: 0,
        shouldShowHumanOption: false,
        currentCategory: undefined,

        // 匿名用户支持
        anonymousId: null,

        /**
         * 发送消息
         * 乐观更新：立即添加到列表，然后异步发送
         * @param content 消息内容
         * @param stream 是否流式响应
         */
        sendMessage: async (content: string, stream: boolean = false) => {
          // 检测是否为延迟创建的会话
          const { pendingSession } = get();
          if (pendingSession) {
            // 首次发送消息时创建会话
            try {
              await get().createSession();
              // 清除 pendingSession 标记
              set({ pendingSession: false });
            } catch (error) {
              console.error('[chatStore] 创建会话失败:', error);
              // 创建失败时，仍然清空 pendingSession
              set({ pendingSession: false });
              throw error;
            }
          }

          const { send: wsSend } = await import('../services/websocket');

          // 创建用户消息
          const userMessage: ChatMessage = {
            id: `msg-${Date.now()}-user`,
            role: 'user',
            content,
            timestamp: new Date(),
            status: 'sending',
          };

          // 立即添加到列表（乐观更新）
          set({ messages: [...get().messages, userMessage] });

          try {
            // 获取当前会话 ID（优先使用 store 中的，这是唯一真相源）
            const currentConvId = get().conversationId;

            await wsSend(content, currentConvId || undefined, stream);

            // 更新状态为已发送
            set({
              messages: get().messages.map(msg =>
                msg.id === userMessage.id
                  ? { ...msg, status: 'sent' as const }
                  : msg
              ),
            });
          } catch (error) {
            console.error('[chatStore] 发送失败:', error);

            // 标记为失败
            set({
              messages: get().messages.map(msg =>
                msg.id === userMessage.id
                  ? { ...msg, status: 'failed' as const }
                  : msg
              ),
            });
          }
        },

        /**
         * 添加消息
         */
        addMessage: (message: ChatMessage) => {
          set({ messages: [...get().messages, message] });
        },

        /**
         * 更新流式消息内容
         */
        updateStreamingMessage: (id: string, content: string) => {
          set({
            messages: get().messages.map(msg =>
              msg.id === id
                ? { ...msg, content: content + (msg.content || ''), isStreaming: true }
                : msg
            ),
          });
        },

        /**
         * 完成流式消息
         * @param id 消息 ID
         * @param intent 意图类型
         * @param confidence 置信度
         * @param suggestedActions 建议操作
         */
        completeStreamingMessage: (
          id: string,
          intent?: string,
          confidence?: number,
          suggestedActions?: string[]
        ) => {
          set({
            messages: get().messages.map(msg =>
              msg.id === id
                ? {
                  ...msg,
                  isStreaming: false,
                  intent,
                  confidence,
                  suggestedActions,
                }
                : msg
            ),
          });
        },

        /**
         * 清空历史记录
         */
        clearHistory: () => {
          set({ messages: [] });
        },

        /**
         * 连接 WebSocket
         */
        connect: () => {
          import('../services/websocket').then(({ connect }) => {
            connect();
          });
        },

        /**
         * 断开 WebSocket
         */
        disconnect: () => {
          import('../services/websocket').then(({ disconnect }) => {
            disconnect();
          });
        },

        /**
         * 设置 AI 打字状态
         */
        setTyping: (isTyping: boolean) => {
          set({ isTyping });
        },

        /**
         * 设置连接状态
         */
        setConnected: (isConnected: boolean) => {
          set({ isConnected });
        },

        /**
         * 设置会话 ID
         */
        setConversationId: (conversationId: string | null) => {
          set({ conversationId });
        },

        /**
         * 设置当前会话 ID（用于会话列表）
         */
        setCurrentSessionId: (sessionId: string | null) => {
          set({ currentSessionId: sessionId });
        },

        /**
         * 创建新会话
         */
        createSession: async () => {
          // 生成新的会话 ID
          const newSessionId = `conv_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

          // 清空当前消息
          set({
            conversationId: newSessionId,
            currentSessionId: newSessionId,
            messages: [],
          });

          return newSessionId;
        },

        /**
         * 获取会话列表
         */
        fetchSessions: async () => {
          set({ isLoadingSessions: true });
          try {
            // 确保有匿名 ID
            const { anonymousId, ensureAnonymousId } = get();
            if (!anonymousId) {
              await ensureAnonymousId();
            }

            // 使用新的 API 方法
            const response = await chatApi.listConversations(1, 50);
            set({ sessions: response.items || [] });
          } catch (error) {
            console.error('[chatStore] 获取会话列表失败:', error);
            // 失败时保持空列表
            set({ sessions: [] });
          } finally {
            set({ isLoadingSessions: false });
          }
        },

        /**
         * 获取历史消息
         */
        fetchHistory: async (sessionId: string) => {
          try {
            const messages = await chatApi.getConversationMessages(sessionId);
            // 转换为 ChatMessage 格式
            const convertedMessages: ChatMessage[] = messages.map((m: Record<string, unknown>) => ({
              id: m.id as string,
              role: m.role as 'user' | 'assistant' | 'system',
              content: m.content as string,
              timestamp: new Date(m.created_at as string),
              intent: m.intent as string | undefined,
              confidence: m.confidence as number | undefined,
            }));
            set({ messages: convertedMessages });
          } catch (error) {
            console.error('[chatStore] 获取历史消息失败:', error);
            set({ messages: [] });
          }
        },

        /**
         * 切换会话
         */
        switchSession: async (sessionId: string) => {
          try {
            // 直接使用 sessionId（即后端的 session_id，如 conv_xxx）
            // 不再调用管理端 API，避免 ID 格式不匹配
            set({
              conversationId: sessionId,
              currentSessionId: sessionId,
            });

            // 同步 WebSocket 服务的 conversation_id，确保发送消息时使用正确的会话 ID
            const { setConversationId: wsSetConversationId } = await import('../services/websocket');
            wsSetConversationId(sessionId);

            // 加载会话的历史消息
            await get().fetchHistory(sessionId);
          } catch (error) {
            console.error('[chatStore] 切换会话失败:', error);
          }
        },

        /**
         * 删除会话
         */
        deleteSession: async (sessionId: string) => {
          try {
            await chatApi.deleteConversation(sessionId);

            // 从列表中移除
            set({
              sessions: get().sessions.filter(s => s.id !== sessionId),
            });

            // 如果删除的是当前会话，清空消息
            if (get().currentSessionId === sessionId) {
              set({
                currentSessionId: null,
                conversationId: null,
                messages: [],
              });
            }
          } catch (error) {
            console.error('[chatStore] 删除会话失败:', error);
          }
        },

        // ========================================================================
        // Widget UI 操作
        // ========================================================================

        /**
         * 打开历史记录模态框
         * 自动获取会话列表
         */
        openHistoryModal: async () => {
          set({ isHistoryModalOpen: true });
          // 获取会话列表
          await get().fetchSessions();
        },

        /**
         * 关闭历史记录模态框
         */
        closeHistoryModal: () => {
          set({ isHistoryModalOpen: false });
        },

        /**
         * 开始新会话（延迟创建）
         * 清空消息列表，但不立即创建会话
         * 等到发送第一条消息时才创建
         */
        startNewSession: () => {
          set({
            messages: [],
            conversationId: null,
            currentSessionId: null,
            pendingSession: true,
          });
        },

        /**
         * 请求删除会话
         * 打开确认对话框
         */
        requestDeleteSession: (sessionId: string) => {
          set({
            deletingSessionId: sessionId,
            isConfirmDialogOpen: true,
          });
        },

        /**
         * 确认删除会话 (或关闭)
         */
        confirmDeleteSession: async (id?: string) => {
          const deletingSessionId = id || get().deletingSessionId;
          if (!deletingSessionId) return;

          try {
            await get().deleteSession(deletingSessionId);
            // 关闭确认对话框 (如果有)
            set({
              deletingSessionId: null,
              isConfirmDialogOpen: false,
            });
          } catch (error) {
            console.error('[chatStore] 确认删除会话失败:', error);
            // 保持确认对话框打开
          }
        },

        /**
         * 取消删除会话
         */
        cancelDeleteSession: () => {
          set({
            deletingSessionId: null,
            isConfirmDialogOpen: false,
          });
        },

        // ========================================================================
        // 人工客服触发控制
        // ========================================================================

        /**
         * 增加失败计数
         * 当达到3次时，自动显示人工客服选项
         */
        incrementFailureCount: () => {
          const newCount = get().failureCount + 1;
          set({
            failureCount: newCount,
            shouldShowHumanOption: newCount >= 3,
          });
        },

        /**
         * 重置失败计数
         * 当用户获得满意回答时调用
         */
        resetFailureCount: () => {
          set({
            failureCount: 0,
            shouldShowHumanOption: false,
          });
        },

        /**
         * 用户点击不满意
         * 增加失败计数
         * 可选：发送事件到后端记录
         */
        reportUnsatisfied: () => {
          get().incrementFailureCount();
          // 可选：发送事件到后端记录
          // console.log('[chatStore] 用户报告不满意');
        },

        /**
         * 手动设置是否显示人工客服选项
         */
        setShouldShowHumanOption: (show: boolean) => {
          set({ shouldShowHumanOption: show });
        },

        /**
         * 设置当前会话类别
         */
        setCurrentCategory: (category: ConversationCategory) => {
          set({ currentCategory: category });
        },

        // ========================================================================
        // 匿名用户支持
        // ========================================================================

        /**
         * 确保匿名 ID 存在
         * 如果不存在则调用后端 API 生成新的匿名 ID
         */
        ensureAnonymousId: async () => {
          const { anonymousId } = get();
          if (anonymousId) {
            return anonymousId;
          }

          try {
            const { ensureAnonymousId: ensureId } = await import('../utils/anonymous');
            const id = await ensureId();
            set({ anonymousId: id });
            return id;
          } catch (error) {
            console.error('[chatStore] 获取匿名 ID 失败:', error);
            throw error;
          }
        },
      }),
      {
        name: 'chat-storage',
        // 持久化 messages、conversationId、failureCount 和 anonymousId
        partialize: (state) => ({
          messages: state.messages,
          conversationId: state.conversationId,
          failureCount: state.failureCount,
          anonymousId: state.anonymousId,
        }),
      }
    )
  )
);
